import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import MemberMobileBookingHeader from '../components/MemberMobileBookingHeader'

function formatBookingHour(value) {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})/)
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : String(value || '')
}

function formatCourseSummary(course, memberText) {
  const parts = []
  if (course?.holes_count) {
    parts.push(memberText("portalPresentation.member__count_holes", { count: course.holes_count }))
  }
  if (course?.booking_hours_start && course?.booking_hours_end) {
    parts.push(`${formatBookingHour(course.booking_hours_start)} – ${formatBookingHour(course.booking_hours_end)}`)
  }
  return parts.join(' · ')
}

export default function MemberGolfLandingPage({ clubCode }) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const navigate = useNavigate()
  const { memberPortalApiGet } = useMemberPortalApi()
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function loadCourses() {
      if (!clubCode) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses`)
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_courses')
        }

        const courseData = Array.isArray(json?.data) ? json.data : []
        if (!cancelled) {
          setCourses(courseData)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load golf courses', err)
          setError("portalPresentation.member_unable_to_load_courses_right_now")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadCourses()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet])

  const activeCourses = useMemo(() => courses.filter((course) => course?.id), [courses])
  const hasSingleCourse = activeCourses.length === 1

  useEffect(() => {
    if (!loading && !error && hasSingleCourse) {
      navigate(`/${clubCode}/golf/${activeCourses[0].id}`, { replace: true })
    }
  }, [activeCourses, clubCode, error, hasSingleCourse, loading, navigate])

  if (loading) {
    return (
      <div className="mp-panel">
        <div className="mp-panel__header">
          <div>
            <p className="mp-eyebrow">{memberText("portalPresentation.member_golf")}</p>
            <h4 className="mp-panel__title">{memberText("golf.teeSheet.loadingCourses")}</h4>
          </div>
        </div>
        <p className="mp-text-muted">{memberText("portalPresentation.member_fetching_available_courses_for_your_club")}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mp-panel">
        <div className="mp-panel__header">
          <div>
            <p className="mp-eyebrow">{memberText("portalPresentation.member_golf")}</p>
            <h4 className="mp-panel__title">{memberText("portalPresentation.member_golf_tee_times")}</h4>
          </div>
        </div>
        <div className="mp-warning">{memberText(error)}</div>
      </div>
    )
  }

  if (hasSingleCourse) {
    return null
  }

  return (
    <div className="mp-panel mp-golf-course-selection">
      <MemberMobileBookingHeader
        className="mp-golf-course-selection__header"
        eyebrow={memberText("portalPresentation.member_golf")}
        title={memberText("portalPresentation.member_choose_your_course")}
        backLabel={memberText("portalPresentation.member_back_to_facilities")}
        backTo={`/${clubCode}/book-facilities`}
      />
      <p className="mp-golf-course-selection__helper">{memberText("portalPresentation.member_select_a_course_to_view_available_tee_times")}</p>

      {activeCourses.length === 0 ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_no_active_courses_are_available_for_booking_yet")}</p>
      ) : (
        <div className="mp-golf-course-grid">
          {activeCourses.map((course) => (
            <button
              type="button"
              key={course.id}
              className={`mp-golf-course-card mp-golf-course-card--selection${course.course_image_url ? ' mp-golf-course-card--with-image' : ''}`}
              style={course.course_image_url ? {
                '--mp-course-card-image': `url(${course.course_image_url})`,
                '--mp-course-card-position': `${course.course_image_focus_x ?? 50}% ${course.course_image_focus_y ?? 50}%`,
              } : undefined}
              onClick={() => navigate(`/${clubCode}/golf/${course.id}`)}
            >
              <div className="mp-golf-course-card__meta">
                <p className="mp-golf-course-label">{memberText("golf.print.course")}</p>
                <div className="mp-golf-course-name">{course.name}</div>
                <div className="mp-golf-course-sub">{formatCourseSummary(course, memberText)}</div>
              </div>
              <div className="mp-golf-course-chip"> {memberText("portalPresentation.member_select")} <ChevronRight size={18} aria-hidden="true" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

MemberGolfLandingPage.propTypes = {
  clubCode: PropTypes.string.isRequired,
}
